// src/pages/user/SingleChildDetails.tsx
// Route: /user/children/:id

import { useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useGetChildQuery } from "../../app/api/parentApi";
import LearnerLoginCard from "../../components/LearnerLoginCard";
import AiKeyCard from "../../components/AiKeyCard";
import useHashScroll from "../../app/useHashScroll";
import { useInitializePaymentMutation } from "../../app/api/paymentApi";
import type { ChildDetail, PaymentSummary } from "../../app/api/parentApi";
// Import Enrolment from enrolmentApi — it has the full billing fields
// (billingPeriod, billingLabel, monthsCovered, grossAmount, discountPercent,
//  discountAmount, nextPaymentDate, nextPaymentLabel)
import type { Enrolment } from "../../app/api/enrolmentApi";

import { ArrowLeft, ArrowRight, BookOpen, Cake, CalendarDays, CircleCheck, ClipboardList, CreditCard, Folder, GraduationCap, LayoutDashboard, Mail, MessageCircle, RefreshCw, Sparkles } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import Ico from "../../components/Ico";
/* ─── CONSTANTS ──────────────────────────────────────────────────── */

const TRACK_LABELS: Record<string, string> = {
  foundation_builders: "Foundation Builders (Ages 6–9)",
  explorers: "Explorers (Ages 9–12)",
  innovators: "Innovators (Ages 13–18)",
  unassigned: "Unassigned",
};
const TRACK_SHORT: Record<string, string> = {
  foundation_builders: "Foundation (6–9)",
  explorers: "Explorers (9–12)",
  innovators: "Innovators (13–18)",
  unassigned: "Unassigned",
};
const TRACK_COLORS: Record<
  string,
  { bg: string; text: string; border: string; accent: string }
> = {
  foundation_builders: {
    bg: "#fff4f1",
    text: "#7a3020",
    border: "#f5c5b0",
    accent: "#efb0a4",
  },
  explorers: {
    bg: "#ebeff7",
    text: "#4a44b0",
    border: "#cdd4e2",
    accent: "#1b2d5e",
  },
  innovators: {
    bg: "#e8ecf5",
    text: "#1b2d5e",
    border: "#c9cfe0",
    accent: "#4a44b0",
  },
  unassigned: {
    bg: "#f1f3f8",
    text: "#8189a0",
    border: "#dde2ec",
    accent: "#a6adbf",
  },
};
const TRACK_DESCRIPTIONS: Record<string, string> = {
  foundation_builders:
    "Scratch, logic, storytelling, Makey Makey, HTML basics, AI literacy, and public speaking confidence. Every session builds the belief that they are capable of creating things that matter.",
  explorers:
    "HTML, CSS, JavaScript, APIs, Arduino, and community app development. By the end, a real deployed website — live on the internet, accessible to anyone.",
  innovators:
    "MERN stack, React Native, IoT, and applied AI. Professional-grade tools used by developers at companies around the world. Every stage builds toward a live, user-tested product.",
  unassigned:
    "Programme track will be assigned based on age and assessment results.",
};
const TRACK_DELIVERABLES: Record<string, string[]> = {
  foundation_builders: [
    "Interactive Scratch story (Q1)",
    "Playable Scratch game (Q2)",
    "Physical controller + first webpage (Q3)",
  ],
  explorers: [
    "Personal portfolio website (Q1)",
    "Live deployed responsive site (Q2)",
    "Community JavaScript app + AI tool (Q3)",
  ],
  innovators: [
    "React developer portfolio on Vercel (Stage 1)",
    "Full-stack MERN app with auth (Stage 2)",
    "Mobile app + IoT dashboard + AI feature (Stage 3)",
    "Innovation capstone + Grand Showcase (Stage 4)",
  ],
  unassigned: [],
};
const STUDENT_STATUS: Record<
  string,
  { label: string; cls: string; desc: string }
> = {
  prospect: {
    label: "Prospect",
    cls: "bg-[#f1f3f8] text-[#8189a0] border border-[#dde2ec]",
    desc: "Added to your account. Awaiting payment to confirm enrolment.",
  },
  enrolled: {
    label: "Enrolled",
    cls: "bg-[#ebeff7] text-[#4a44b0] border border-[#cdd4e2]",
    desc: "Payment confirmed. Ready to start or currently between quarters.",
  },
  active: {
    label: "Active",
    cls: "bg-[#edfaf4] text-[#2a7a56] border border-[#b6e8d0]",
    desc: "Currently attending sessions and building their project.",
  },
  inactive: {
    label: "Inactive",
    cls: "bg-[#fff3f3] text-[#9b2a2a] border border-[#f5c0c0]",
    desc: "Not currently enrolled. Contact the academy to re-activate.",
  },
};
const PAY_STATUS: Record<string, { label: string; cls: string }> = {
  paid: {
    label: "Paid",
    cls: "bg-[#edfaf4] text-[#2a7a56] border border-[#b6e8d0]",
  },
  pending: {
    label: "Pending",
    cls: "bg-[#fff8ee] text-[#7a4a10] border border-[#f5d9a0]",
  },
  overdue: {
    label: "Overdue",
    cls: "bg-[#fff3f3] text-[#9b2a2a] border border-[#f5c0c0]",
  },
  waived: {
    label: "Waived",
    cls: "bg-[#ebeff7] text-[#4a44b0] border border-[#cdd4e2]",
  },
  failed: {
    label: "Failed",
    cls: "bg-[#fff3f3] text-[#9b2a2a] border border-[#f5c0c0]",
  },
};
const ENROL_STATUS: Record<string, { label: string; cls: string }> = {
  pending: {
    label: "Pending payment",
    cls: "bg-[#fff8ee] text-[#7a4a10] border border-[#f5d9a0]",
  },
  confirmed: {
    label: "Confirmed",
    cls: "bg-[#edfaf4] text-[#2a7a56] border border-[#b6e8d0]",
  },
  completed: {
    label: "Completed",
    cls: "bg-[#ebeff7] text-[#4a44b0] border border-[#cdd4e2]",
  },
  withdrawn: {
    label: "Withdrawn",
    cls: "bg-[#fff3f3] text-[#9b2a2a] border border-[#f5c0c0]",
  },
};
const BILLING_CONFIG: Record<
  string,
  { bg: string; text: string; border: string }
> = {
  monthly: { bg: "#fff8ee", text: "#7a4a10", border: "#f5d9a0" },
  quarterly: { bg: "#ebeff7", text: "#4a44b0", border: "#cdd4e2" },
  annually: { bg: "#edfaf4", text: "#2a7a56", border: "#b6e8d0" },
};

/* ─── HELPERS ────────────────────────────────────────────────────── */

function formatDate(
  iso: string | null | undefined,
  opts?: Intl.DateTimeFormatOptions,
): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    ...opts,
  });
}
function initials(a: string, b: string) {
  return `${a.charAt(0)}${b.charAt(0)}`.toUpperCase();
}
function formatNaira(n: number) {
  return `₦${Number(n).toLocaleString("en-NG")}`;
}

/* ─── ATOMS ──────────────────────────────────────────────────────── */

function Chip({ label, cls }: { label: string; cls: string }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[12.5px] font-semibold ${cls}`}
    >
      {label}
    </span>
  );
}
function Card({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`rounded-[24px] border border-[#e5e8ef] bg-white shadow-[0_4px_16px_rgba(27,45,94,0.05)] ${className}`}
    >
      {children}
    </div>
  );
}
function ProgressBar({
  pct,
  color = "#1b2d5e",
}: {
  pct: number;
  color?: string;
}) {
  return (
    <div className="h-2 overflow-hidden rounded-full bg-[#ebeff7]">
      <div
        className="h-full rounded-full transition-all duration-700"
        style={{
          width: `${Math.min(100, Math.max(0, pct))}%`,
          background: color,
        }}
      />
    </div>
  );
}
function Skeleton({ className = "" }: { className?: string }) {
  return (
    <div className={`animate-pulse rounded-2xl bg-[#ebeff7] ${className}`} />
  );
}
function Spinner() {
  return (
    <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z"
      />
    </svg>
  );
}
function InfoRow({
  icon,
  label,
  value,
  valueClass = "",
}: {
  icon: LucideIcon;
  label: string;
  value: React.ReactNode;
  valueClass?: string;
}) {
  return (
    <div className="flex items-start gap-3 py-3">
      <span className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-xl bg-[#f1f3f8] text-sm">
        <Ico icon={icon} />
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-[11.5px] font-semibold uppercase tracking-wider text-[#8189a0]">
          {label}
        </p>
        <p
          className={`mt-0.5 text-sm font-semibold text-[#16213e] ${valueClass}`}
        >
          {value}
        </p>
      </div>
    </div>
  );
}

/* ─── HERO HEADER ────────────────────────────────────────────────── */

function HeroHeader({ child }: { child: ChildDetail }) {
  const tc = TRACK_COLORS[child.track] ?? TRACK_COLORS.unassigned;
  const si = STUDENT_STATUS[child.studentStatus] ?? STUDENT_STATUS.prospect;
  return (
    <Card className="overflow-hidden">
      <div
        className="h-3 w-full"
        style={{
          background: `linear-gradient(90deg, ${tc.accent}, ${tc.accent}88)`,
        }}
      />
      <div className="flex flex-col gap-5 p-6 sm:flex-row sm:items-start">
        <span
          className="flex h-20 w-20 flex-shrink-0 items-center justify-center rounded-[20px] text-2xl font-bold"
          style={{
            background: tc.bg,
            color: tc.text,
            border: `2px solid ${tc.border}`,
          }}
        >
          {initials(child.firstName, child.lastName)}
        </span>
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold tracking-[-0.04em] text-[#16213e]">
            {child.firstName} {child.lastName}
          </h1>
          <p className="mt-0.5 text-sm text-[#8189a0]">Age {child.age}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <span
              className="inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold"
              style={{
                background: tc.bg,
                color: tc.text,
                border: `1px solid ${tc.border}`,
              }}
            >
              <BookOpen className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}{TRACK_SHORT[child.track] ?? child.track}
            </span>
            <Chip label={si.label} cls={si.cls} />
          </div>
          <p className="mt-2.5 text-xs leading-5 text-[#5f6780]">{si.desc}</p>
        </div>
      </div>
    </Card>
  );
}

/* ─── IDENTITY CARD ──────────────────────────────────────────────── */

function IdentityCard({ child }: { child: ChildDetail }) {
  return (
    <Card className="p-6">
      <p className="mb-3 text-[11.5px] font-bold uppercase tracking-[0.2em] text-[#8189a0]">
        Profile details
      </p>
      <div className="divide-y divide-[#f7f8fb]">
        <InfoRow
          icon={Cake}
          label="Date of birth"
          value={new Date(child.dateOfBirth).toLocaleDateString("en-GB", {
            day: "numeric",
            month: "long",
            year: "numeric",
          })}
        />
        <InfoRow icon={CalendarDays} label="Age" value={`${child.age} years old`} />
        <InfoRow
          icon={Mail}
          label="Email"
          value={child.email ?? "Not provided"}
          valueClass={!child.email ? "text-[#a6adbf] font-normal italic" : ""}
        />
        <InfoRow
          icon={CalendarDays}
          label="Added on"
          value={formatDate(child.createdAt)}
        />
        <InfoRow
          icon={GraduationCap}
          label="Programme track"
          value={TRACK_LABELS[child.track] ?? child.track}
        />
      </div>
    </Card>
  );
}

/* ─── ACTIVE ENROLMENT CARD ──────────────────────────────────────── */

function ActiveEnrolmentCard({ enrolment }: { enrolment: Enrolment }) {
  const [initializePayment, { isLoading: payLoading }] =
    useInitializePaymentMutation();
  const tc = TRACK_COLORS[enrolment.track] ?? TRACK_COLORS.unassigned;
  const billingCfg = enrolment.billingPeriod
    ? BILLING_CONFIG[enrolment.billingPeriod]
    : null;

  const handlePayNow = async () => {
    try {
      const result = await initializePayment({
        enrolmentId: enrolment.id,
      }).unwrap();
      window.location.href = result.data.authorizationUrl;
    } catch (err) {
      console.error("Payment initiation failed:", err);
    }
  };

  return (
    <Card className="overflow-hidden">
      {/* Gradient header */}
      <div
        className="p-5"
        style={{
          background: `linear-gradient(135deg, ${tc.accent}22, ${tc.accent}08)`,
          borderBottom: `1px solid ${tc.border}`,
        }}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <p
              className="text-[11.5px] font-bold uppercase tracking-[0.2em]"
              style={{ color: tc.text }}
            >
              Active enrolment
            </p>
            <h3 className="mt-1 text-lg font-bold tracking-[-0.03em] text-[#16213e]">
              {enrolment.quarter} · {enrolment.academicYear}
            </h3>
            <p className="mt-0.5 text-sm text-[#5f6780]">
              {TRACK_SHORT[enrolment.track] ?? enrolment.track}
              {enrolment.stage ? ` · ${enrolment.stage}` : ""}
            </p>
          </div>
          <div className="flex flex-col items-end gap-1.5">
            <Chip
              label={ENROL_STATUS[enrolment.status]?.label ?? enrolment.status}
              cls={
                ENROL_STATUS[enrolment.status]?.cls ??
                "bg-[#f1f3f8] text-[#8189a0] border border-[#dde2ec]"
              }
            />
            <Chip
              label={
                PAY_STATUS[enrolment.paymentStatus]?.label ??
                enrolment.paymentStatus
              }
              cls={
                PAY_STATUS[enrolment.paymentStatus]?.cls ??
                "bg-[#f1f3f8] text-[#8189a0] border border-[#dde2ec]"
              }
            />
          </div>
        </div>
      </div>

      <div className="p-5">
        {/* Billing plan + pricing */}
        {enrolment.billingPeriod && (
          <div className="mb-4 rounded-[16px] border border-[#ebeff7] bg-[#f9fafc] overflow-hidden">
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#ebeff7]">
              <p className="text-[11.5px] font-bold uppercase tracking-wider text-[#8189a0]">
                Billing plan
              </p>
              {billingCfg && (
                <span
                  className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[12.5px] font-bold"
                  style={{
                    background: billingCfg.bg,
                    color: billingCfg.text,
                    border: `1px solid ${billingCfg.border}`,
                  }}
                >
                  {enrolment.billingLabel ?? enrolment.billingPeriod}
                </span>
              )}
            </div>
            <div className="divide-y divide-[#f7f8fb] text-xs">
              {enrolment.grossAmount !== null &&
                enrolment.discountPercent > 0 && (
                  <>
                    <div className="flex justify-between px-4 py-2 text-[#5a6278]">
                      <span>
                        {enrolment.billingLabel} · {enrolment.monthsCovered}{" "}
                        month{(enrolment.monthsCovered ?? 1) > 1 ? "s" : ""}
                      </span>
                      <span>
                        {enrolment.grossAmount !== null
                          ? formatNaira(enrolment.grossAmount)
                          : "—"}
                      </span>
                    </div>
                    <div className="flex justify-between px-4 py-2 font-semibold text-[#6cc2a0]">
                      <span>
                        {enrolment.billingLabel} discount (
                        {enrolment.discountPercent}%)
                      </span>
                      <span>−{formatNaira(enrolment.discountAmount)}</span>
                    </div>
                    {(enrolment.familyDiscountPercent ?? 0) > 0 && (
                      <div className="flex justify-between px-4 py-2 font-semibold text-[#6cc2a0]">
                        <span>Family discount ({enrolment.familyDiscountPercent}%)</span>
                        <span>−{formatNaira(enrolment.familyDiscountAmount ?? 0)}</span>
                      </div>
                    )}
                  </>
                )}
              <div className="flex justify-between px-4 py-2 font-bold text-[#16213e]">
                <span>Fee paid</span>
                <span>{formatNaira(enrolment.feeAmount)}</span>
              </div>
            </div>
          </div>
        )}

        {/* Progress */}
        <div className="mb-4">
          <div className="mb-1.5 flex items-center justify-between text-xs">
            <span className="font-semibold text-[#3b4560]">
              Week {enrolment.currentWeek}
            </span>
            <span className="font-bold" style={{ color: tc.accent }}>
              {enrolment.progressPercent}%
            </span>
          </div>
          <ProgressBar pct={enrolment.progressPercent} color={tc.accent} />
        </div>

        {/* Current project */}
        {enrolment.currentProject && (
          <div className="mb-4 flex items-center gap-3 rounded-[16px] bg-[#f9fafc] px-4 py-3.5 border border-[#ebeff7]">
            <Folder className="h-5 w-5 flex-shrink-0" aria-hidden />
            <div>
              <p className="text-[11.5px] font-semibold uppercase tracking-wider text-[#8189a0]">
                Current project
              </p>
              <p className="mt-0.5 text-sm font-bold text-[#16213e]">
                {enrolment.currentProject}
              </p>
            </div>
          </div>
        )}

        {/* 3-col detail grid */}
        <div className="grid grid-cols-3 gap-3 text-center">
          {[
            {
              label: "Next session",
              value: enrolment.nextSessionLabel ?? "TBC",
              icon: CalendarDays,
            },
            {
              label: "Confirmed",
              value: formatDate(enrolment.confirmedAt, {
                month: "short",
                day: "numeric",
              }),
              icon: CircleCheck,
            },
            {
              label: "Next payment",
              value: enrolment.nextPaymentLabel ?? "—",
              icon: RefreshCw,
            },
          ].map((item) => (
            <div
              key={item.label}
              className="rounded-[14px] border border-[#ebeff7] bg-[#f9fafc] px-3 py-3"
            >
              <span className="text-base"><Ico icon={item.icon} /></span>
              <p className="mt-1.5 text-[11.5px] font-semibold uppercase tracking-wider text-[#8189a0]">
                {item.label}
              </p>
              <p className="mt-0.5 text-xs font-bold text-[#16213e] break-words">
                {item.value}
              </p>
            </div>
          ))}
        </div>

        {/* Pay now CTA */}
        {enrolment.paymentStatus === "pending" && (
          <button
            onClick={handlePayNow}
            disabled={payLoading}
            className="mt-4 flex w-full items-center justify-center gap-2 rounded-2xl bg-[#1b2d5e] py-3 text-sm font-semibold text-white shadow-[0_8px_24px_rgba(27,45,94,0.28)] hover:bg-[#24397a] disabled:opacity-60"
          >
            {payLoading ? (
              <>
                <Spinner /> Processing…
              </>
            ) : (
              <><CreditCard className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden /> Pay enrolment fee</>
            )}
          </button>
        )}

        {enrolment.notes && (
          <div className="mt-4 rounded-[14px] border border-[#dde2ec] bg-[#f9fafc] px-4 py-3">
            <p className="text-[11.5px] font-semibold uppercase tracking-wider text-[#8189a0]">
              Admin note
            </p>
            <p className="mt-1 text-sm leading-5 text-[#5a6278]">
              {enrolment.notes}
            </p>
          </div>
        )}
      </div>
    </Card>
  );
}

/* ─── ENROLMENT TIMELINE ─────────────────────────────────────────── */

function EnrolmentTimeline({ enrolments }: { enrolments: Enrolment[] }) {
  const [expanded, setExpanded] = useState<string | null>(null);

  if (enrolments.length === 0) {
    return (
      <Card className="px-6 py-8 text-center">
        <p className="text-sm font-semibold text-[#16213e]">
          No enrolment history
        </p>
        <p className="mt-1 text-xs text-[#8189a0]">
          Once enrolment is confirmed, each quarter will appear here.
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {enrolments.map((e, idx) => {
        const isOpen = expanded === e.id;
        const tc = TRACK_COLORS[e.track] ?? TRACK_COLORS.unassigned;
        const isActive = e.status === "confirmed" || e.status === "pending";
        const billingCfg = e.billingPeriod
          ? BILLING_CONFIG[e.billingPeriod]
          : null;

        return (
          <div
            key={e.id}
            className={`rounded-[20px] border bg-white shadow-[0_2px_8px_rgba(27,45,94,0.04)] transition-shadow ${isOpen ? "shadow-[0_6px_20px_rgba(27,45,94,0.09)]" : ""}`}
            style={
              isActive ? { borderColor: tc.border } : { borderColor: "#e5e8ef" }
            }
          >
            {/* Row header */}
            <button
              onClick={() => setExpanded(isOpen ? null : e.id)}
              className="flex w-full items-center gap-4 px-5 py-4 text-left"
              aria-expanded={isOpen}
            >
              {/* Timeline dot */}
              <div className="relative flex flex-shrink-0 flex-col items-center">
                <span
                  className="h-3 w-3 rounded-full"
                  style={{
                    background: isActive ? tc.accent : "#dde2ec",
                    boxShadow: isActive ? `0 0 0 4px ${tc.accent}22` : "none",
                  }}
                />
                {idx < enrolments.length - 1 && (
                  <span className="absolute top-4 h-6 w-px bg-[#e5e8ef]" />
                )}
              </div>

              {/* Labels */}
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-bold text-[#16213e]">
                    {e.quarter} · {e.academicYear}
                  </span>
                  {/* Enrolment status */}
                  <Chip
                    label={ENROL_STATUS[e.status]?.label ?? e.status}
                    cls={
                      ENROL_STATUS[e.status]?.cls ??
                      "bg-[#f1f3f8] text-[#8189a0] border border-[#dde2ec]"
                    }
                  />
                  {/* Payment status */}
                  <Chip
                    label={
                      PAY_STATUS[e.paymentStatus]?.label ?? e.paymentStatus
                    }
                    cls={
                      PAY_STATUS[e.paymentStatus]?.cls ??
                      "bg-[#f1f3f8] text-[#8189a0] border border-[#dde2ec]"
                    }
                  />
                  {/* Billing plan */}
                  {billingCfg && (
                    <span
                      className="inline-flex items-center rounded-full px-2 py-0.5 text-[11.5px] font-bold"
                      style={{
                        background: billingCfg.bg,
                        color: billingCfg.text,
                        border: `1px solid ${billingCfg.border}`,
                      }}
                    >
                      {e.billingLabel}
                    </span>
                  )}
                </div>
                <p className="mt-0.5 text-xs text-[#8189a0]">
                  {TRACK_SHORT[e.track] ?? e.track}
                  {e.stage ? ` · ${e.stage}` : ""}
                  {" · "} Week {e.currentWeek} · {e.progressPercent}% complete
                </p>
              </div>

              {/* Fee + discount */}
              <div className="flex-shrink-0 text-right">
                <p className="text-sm font-bold text-[#16213e]">
                  {formatNaira(e.feeAmount)}
                </p>
                {e.discountPercent > 0 && (
                  <p className="text-[11.5px] font-semibold text-[#6cc2a0]">
                    {e.discountPercent}% off
                  </p>
                )}
              </div>

              <svg
                className={`h-4 w-4 flex-shrink-0 text-[#8189a0] transition-transform ${isOpen ? "rotate-180" : ""}`}
                viewBox="0 0 16 16"
                fill="none"
              >
                <path
                  d="M4 6l4 4 4-4"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>

            {/* Expanded detail */}
            {isOpen && (
              <div className="border-t border-[#f7f8fb] px-5 pb-5 pt-4 space-y-4">
                {/* Pricing breakdown */}
                {e.billingPeriod && (
                  <div className="rounded-[16px] border border-[#ebeff7] bg-[#f9fafc] overflow-hidden">
                    <p className="px-4 py-2.5 text-[11.5px] font-bold uppercase tracking-wider text-[#8189a0] border-b border-[#ebeff7]">
                      Billing breakdown
                    </p>
                    <div className="divide-y divide-[#f7f8fb] text-xs">
                      {e.grossAmount !== null && e.discountPercent > 0 && (
                        <>
                          <div className="flex justify-between px-4 py-2 text-[#5a6278]">
                            <span>
                              {e.billingLabel} · {e.monthsCovered} month
                              {(e.monthsCovered ?? 1) > 1 ? "s" : ""}
                            </span>
                            <span>
                              {e.grossAmount !== null
                                ? formatNaira(e.grossAmount)
                                : "—"}
                            </span>
                          </div>
                          <div className="flex justify-between px-4 py-2 font-semibold text-[#6cc2a0]">
                            <span>
                              {e.billingLabel} discount ({e.discountPercent}%)
                            </span>
                            <span>−{formatNaira(e.discountAmount)}</span>
                          </div>
                          {(e.familyDiscountPercent ?? 0) > 0 && (
                            <div className="flex justify-between px-4 py-2 font-semibold text-[#6cc2a0]">
                              <span>Family discount ({e.familyDiscountPercent}%)</span>
                              <span>−{formatNaira(e.familyDiscountAmount ?? 0)}</span>
                            </div>
                          )}
                        </>
                      )}
                      <div className="flex justify-between px-4 py-2 font-bold text-[#16213e]">
                        <span>Total charged</span>
                        <span>{formatNaira(e.feeAmount)}</span>
                      </div>
                      {e.nextPaymentLabel && e.status === "confirmed" && (
                        <div className="flex justify-between px-4 py-2 text-[#5a6278]">
                          <span>Next payment due</span>
                          <span className="font-semibold text-[#1b2d5e]">
                            <CalendarDays className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}{e.nextPaymentLabel}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Progress */}
                <div>
                  <div className="mb-1.5 flex justify-between text-xs">
                    <span className="font-medium text-[#5a6278]">
                      Quarter progress
                    </span>
                    <span className="font-bold" style={{ color: tc.accent }}>
                      {e.progressPercent}%
                    </span>
                  </div>
                  <ProgressBar pct={e.progressPercent} color={tc.accent} />
                </div>

                {/* Detail grid */}
                <div className="grid gap-2 sm:grid-cols-2">
                  {[
                    {
                      label: "Current project",
                      value: e.currentProject ?? "—",
                    },
                    {
                      label: "Next session",
                      value: e.nextSessionLabel ?? "TBC",
                    },
                    { label: "Confirmed", value: formatDate(e.confirmedAt) },
                    { label: "Last updated", value: formatDate(e.updatedAt) },
                  ].map((row) => (
                    <div
                      key={row.label}
                      className="rounded-[12px] bg-[#f9fafc] px-3.5 py-3"
                    >
                      <p className="text-[11.5px] font-semibold uppercase tracking-wider text-[#8189a0]">
                        {row.label}
                      </p>
                      <p className="mt-0.5 text-sm font-semibold text-[#16213e]">
                        {row.value}
                      </p>
                    </div>
                  ))}
                </div>

                {/* Payments linked to this enrolment */}
                {e.payments && e.payments.length > 0 && (
                  <div>
                    <p className="mb-2 text-[11.5px] font-bold uppercase tracking-wider text-[#8189a0]">
                      Payments for this enrolment
                    </p>
                    <div className="space-y-2">
                      {e.payments.map((p) => (
                        <div
                          key={p.id}
                          className="flex items-center justify-between gap-3 rounded-[12px] border border-[#ebeff7] bg-white px-4 py-2.5"
                        >
                          <div>
                            <p className="text-sm font-semibold text-[#16213e]">
                              {p.desc}
                            </p>
                            <p className="text-[12.5px] text-[#8189a0]">
                              {p.date}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-bold text-[#16213e]">
                              {p.amount}
                            </span>
                            <Chip
                              label={PAY_STATUS[p.status]?.label ?? p.status}
                              cls={PAY_STATUS[p.status]?.cls ?? ""}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {e.notes && (
                  <div className="rounded-[12px] border border-[#dde2ec] bg-[#f9fafc] px-4 py-3">
                    <p className="text-[11.5px] font-semibold uppercase tracking-wider text-[#8189a0]">
                      Admin note
                    </p>
                    <p className="mt-1 text-sm leading-5 text-[#5a6278]">
                      {e.notes}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ─── PAYMENT TABLE ──────────────────────────────────────────────── */

function PaymentTable({ payments }: { payments: PaymentSummary[] }) {
  if (payments.length === 0) {
    return (
      <Card className="px-6 py-8 text-center">
        <p className="text-sm font-semibold text-[#16213e]">
          No payment records
        </p>
        <p className="mt-1 text-xs text-[#8189a0]">
          Payment records will appear once an enrolment fee is initiated.
        </p>
      </Card>
    );
  }

  const totalPaid = payments
    .filter((p) => p.status === "paid")
    .reduce((s, p) => s + (p.amountRaw ?? 0), 0);
  const hasPending = payments.some(
    (p) => p.status === "pending" || p.status === "overdue",
  );

  return (
    <Card>
      <div className="flex items-center justify-between border-b border-[#f7f8fb] px-5 py-4">
        <div>
          <p className="text-[11.5px] font-bold uppercase tracking-wider text-[#8189a0]">
            Total paid
          </p>
          <p className="mt-0.5 text-xl font-bold tracking-[-0.03em] text-[#16213e]">
            {formatNaira(totalPaid)}
          </p>
        </div>
        {hasPending && (
          <span className="inline-flex items-center gap-1.5 rounded-full border border-[#f5d9a0] bg-[#fff8ee] px-3 py-1.5 text-[12.5px] font-semibold text-[#7a4a10]">
            <span className="h-1.5 w-1.5 rounded-full bg-[#f0a050]" /> Payment
            due
          </span>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-[#f7f8fb]">
              {["Description", "Amount", "Plan", "Date", "Status"].map((h) => (
                <th
                  key={h}
                  className="px-5 py-3 text-[11.5px] font-bold uppercase tracking-wider text-[#8189a0]"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-[#f7f8fb]">
            {payments.map((p) => {
              const enr =
                p.enrolment && typeof p.enrolment === "object"
                  ? (p.enrolment as any)
                  : null;
              const billingCfg = enr?.billingPeriod
                ? BILLING_CONFIG[enr.billingPeriod]
                : null;
              return (
                <tr key={p.id} className="transition-colors hover:bg-[#f9fafc]">
                  <td className="px-5 py-3.5">
                    <p className="font-semibold text-[#16213e]">{p.desc}</p>
                    {enr?.quarter && (
                      <p className="text-[12.5px] text-[#8189a0]">
                        {enr.quarter} · {enr.academicYear}
                      </p>
                    )}
                  </td>
                  <td className="px-5 py-3.5">
                    <p className="font-bold text-[#16213e]">{p.amount}</p>
                    {enr?.discountPercent > 0 && (
                      <p className="text-[11.5px] font-semibold text-[#6cc2a0]">
                        {enr.discountPercent}% off
                      </p>
                    )}
                  </td>
                  <td className="px-5 py-3.5">
                    {billingCfg ? (
                      <span
                        className="inline-flex items-center rounded-full px-2 py-0.5 text-[11.5px] font-bold"
                        style={{
                          background: billingCfg.bg,
                          color: billingCfg.text,
                          border: `1px solid ${billingCfg.border}`,
                        }}
                      >
                        {enr.billingLabel}
                      </span>
                    ) : (
                      <span className="text-xs text-[#8189a0]">—</span>
                    )}
                  </td>
                  <td className="px-5 py-3.5 text-[#8189a0] text-xs">
                    {p.date}
                  </td>
                  <td className="px-5 py-3.5">
                    <Chip
                      label={PAY_STATUS[p.status]?.label ?? p.status}
                      cls={PAY_STATUS[p.status]?.cls ?? ""}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

/* ─── PROGRAMME JOURNEY ──────────────────────────────────────────── */

function ProgrammeJourney({ track }: { track: string }) {
  const tc = TRACK_COLORS[track] ?? TRACK_COLORS.unassigned;
  const description = TRACK_DESCRIPTIONS[track] ?? "";
  const deliverables = TRACK_DELIVERABLES[track] ?? [];
  if (!description) return null;
  return (
    <Card className="overflow-hidden">
      <div
        className="px-6 py-5"
        style={{
          background: `linear-gradient(135deg, ${tc.bg}, white)`,
          borderBottom: `1px solid ${tc.border}`,
        }}
      >
        <p
          className="text-[11.5px] font-bold uppercase tracking-[0.2em]"
          style={{ color: tc.text }}
        >
          About this programme
        </p>
        <h3 className="mt-1.5 text-lg font-bold tracking-[-0.03em] text-[#16213e]">
          {TRACK_LABELS[track] ?? track}
        </h3>
      </div>
      <div className="p-6">
        <p className="text-sm leading-7 text-[#5a6278]">{description}</p>
        {deliverables.length > 0 && (
          <div className="mt-5">
            <p className="mb-3 text-[11.5px] font-bold uppercase tracking-[0.2em] text-[#8189a0]">
              What they will build
            </p>
            <div className="space-y-2">
              {deliverables.map((item, i) => (
                <div
                  key={i}
                  className="flex items-center gap-3 rounded-[14px] border border-[#ebeff7] bg-[#f9fafc] px-4 py-3"
                >
                  <span
                    className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-[11.5px] font-bold text-white"
                    style={{ background: tc.accent }}
                  >
                    {i + 1}
                  </span>
                  <span className="text-sm font-medium text-[#3b4560]">
                    {item}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
        <div
          className="mt-5 rounded-[16px] px-4 py-4"
          style={{ background: tc.bg, border: `1px solid ${tc.border}` }}
        >
          <p
            className="text-[11.5px] font-bold uppercase tracking-wider"
            style={{ color: tc.text }}
          >
            The three questions — ask every session
          </p>
          <p className="mt-2 text-sm leading-6 font-medium text-[#3b4560]">
            "What did you make? Who does it help? What did you learn?"
          </p>
        </div>
      </div>
    </Card>
  );
}

/* ─── PAGE SKELETON ──────────────────────────────────────────────── */

function PageSkeleton() {
  return (
    <div className="space-y-5">
      <Skeleton className="h-4 w-24" />
      <div className="rounded-[24px] border border-[#e5e8ef] bg-white p-6">
        <div className="flex gap-4">
          <Skeleton className="h-20 w-20 flex-shrink-0 rounded-[20px]" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-6 w-2/5" />
            <Skeleton className="h-3.5 w-1/4" />
            <div className="flex gap-2 pt-1">
              <Skeleton className="h-5 w-24 rounded-full" />
              <Skeleton className="h-5 w-16 rounded-full" />
            </div>
          </div>
        </div>
      </div>
      <div className="grid gap-5 lg:grid-cols-3">
        <div className="space-y-5 lg:col-span-2">
          <Skeleton className="h-48 rounded-[24px]" />
          <Skeleton className="h-64 rounded-[24px]" />
        </div>
        <div className="space-y-5">
          <Skeleton className="h-52 rounded-[24px]" />
          <Skeleton className="h-64 rounded-[24px]" />
        </div>
      </div>
    </div>
  );
}

/* ─── TABS ───────────────────────────────────────────────────────── */

type TabKey = "overview" | "enrolments" | "payments" | "programme";
const TABS: { key: TabKey; label: string; icon: LucideIcon }[] = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "enrolments", label: "Enrolments", icon: Sparkles },
  { key: "payments", label: "Payments", icon: CreditCard },
  { key: "programme", label: "Programme", icon: BookOpen },
];

/* ─── ROOT PAGE ──────────────────────────────────────────────────── */

export default function SingleChildDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabKey>("overview");

  const { data, isLoading, isError, refetch } = useGetChildQuery(id ?? "", {
    skip: !id,
  });
  const child = data?.data;
  useHashScroll(!!child);

  const activeEnrolment =
    (child?.enrolments as unknown as Enrolment[])?.find(
      (e) => e.status === "confirmed" || e.status === "pending",
    ) ?? null;

  if (isError)
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center gap-4 text-center">
        <p className="text-sm font-semibold text-[#16213e]">
          Couldn't load this child's profile
        </p>
        <div className="flex gap-3">
          <button
            onClick={refetch}
            className="rounded-2xl bg-[#1b2d5e] px-5 py-2.5 text-xs font-semibold text-white"
          >
            Retry
          </button>
          <Link
            to="/user/children"
            className="rounded-2xl border border-[#dde2ec] px-5 py-2.5 text-xs font-semibold text-[#5a6278]"
          >
            Back to children
          </Link>
        </div>
      </div>
    );

  if (isLoading || !child) return <PageSkeleton />;

  return (
    <div className="space-y-5">
      {/* Back nav */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 text-xs font-semibold text-[#8189a0] hover:text-[#16213e]"
        >
          <svg className="h-3.5 w-3.5" viewBox="0 0 16 16" fill="none">
            <path
              d="M10 4L6 8l4 4"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          My Children
        </button>
        <span className="text-[#c9cfe0]">/</span>
        <span className="text-xs font-semibold text-[#16213e]">
          {child.firstName} {child.lastName}
        </span>
      </div>

      <HeroHeader child={child} />

      {/* Learning access: the child's own sign-in (9+) and FSL AI key (9–12) */}
      {(child.track === "explorers" || child.track === "innovators") && (
        <section id="learning" className="scroll-mt-24 space-y-3">
          <h2 className="text-base font-bold text-[#16213e]">Learning access</h2>
          <div className="grid gap-4 lg:grid-cols-2">
            <LearnerLoginCard childId={child.id} />
            <AiKeyCard childId={child.id} />
          </div>
        </section>
      )}

      {/* Tab bar */}
      <div className="flex gap-1 overflow-x-auto rounded-2xl border border-[#e5e8ef] bg-white p-1.5">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={[
              "flex flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-xl px-3 py-2.5 text-xs font-semibold transition-all",
              activeTab === tab.key
                ? "bg-[#1b2d5e] text-white shadow-[0_4px_12px_rgba(27,45,94,0.30)]"
                : "text-[#8189a0] hover:bg-[#f1f3f8] hover:text-[#16213e]",
            ].join(" ")}
          >
            <span><Ico icon={tab.icon} /></span>
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* OVERVIEW */}
      {activeTab === "overview" && (
        <div className="grid gap-5 lg:grid-cols-3">
          <div className="lg:col-span-1">
            <IdentityCard child={child} />
          </div>
          <div className="lg:col-span-2">
            {activeEnrolment ? (
              <ActiveEnrolmentCard enrolment={activeEnrolment} />
            ) : (
              <Card className="flex flex-col items-center justify-center px-6 py-14 text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[#ebeff7] text-2xl">
                  <ClipboardList className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />
                </span>
                <p className="mt-4 text-sm font-bold text-[#16213e]">
                  Not currently enrolled
                </p>
                <p className="mt-1.5 max-w-xs text-xs leading-5 text-[#5f6780]">
                  {child.studentStatus === "prospect"
                    ? "Complete the enrolment process to confirm their place."
                    : "Contact the academy to set up a new quarter enrolment."}
                </p>
                <Link
                  to="/user/enrolments"
                  className="mt-5 inline-flex items-center gap-1.5 rounded-2xl bg-[#1b2d5e] px-5 py-2.5 text-xs font-semibold text-white shadow-[0_4px_16px_rgba(27,45,94,0.28)] hover:bg-[#24397a]"
                >
                  View enrolments{" "}<ArrowRight className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />
                </Link>
              </Card>
            )}
          </div>
        </div>
      )}

      {/* ENROLMENTS */}
      {activeTab === "enrolments" && (
        <div>
          <div className="mb-4 flex items-center justify-between">
            <p className="text-sm font-bold text-[#16213e]">
              {child.enrolments.length}{" "}
              {child.enrolments.length === 1 ? "enrolment" : "enrolments"}
            </p>
            {child.enrolments.length > 0 && (
              <p className="text-xs text-[#8189a0]">
                Click a row to expand details
              </p>
            )}
          </div>
          <EnrolmentTimeline
            enrolments={child.enrolments as unknown as Enrolment[]}
          />
        </div>
      )}

      {/* PAYMENTS */}
      {activeTab === "payments" && (
        <div>
          <div className="mb-4">
            <p className="text-sm font-bold text-[#16213e]">
              {child.payments.length}{" "}
              {child.payments.length === 1
                ? "payment record"
                : "payment records"}
            </p>
          </div>
          <PaymentTable payments={child.payments} />
        </div>
      )}

      {/* PROGRAMME */}
      {activeTab === "programme" && <ProgrammeJourney track={child.track} />}

      {/* Bottom actions */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between rounded-[24px] border border-[#e5e8ef] bg-white px-5 py-4">
        <div className="flex flex-wrap gap-2">
          <Link
            to="/user/messages"
            className="inline-flex items-center gap-1.5 rounded-2xl border border-[#dde2ec] px-4 py-2 text-xs font-semibold text-[#3b4560] hover:bg-[#f1f3f8]"
          >
            <MessageCircle className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}Message tutor
          </Link>
          <Link
            to="/user/enrolments"
            className="inline-flex items-center gap-1.5 rounded-2xl border border-[#dde2ec] px-4 py-2 text-xs font-semibold text-[#3b4560] hover:bg-[#f1f3f8]"
          >
            <ClipboardList className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}Enrolments
          </Link>
          <Link
            to="/user/payments"
            className="inline-flex items-center gap-1.5 rounded-2xl border border-[#dde2ec] px-4 py-2 text-xs font-semibold text-[#3b4560] hover:bg-[#f1f3f8]"
          >
            <CreditCard className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}All payments
          </Link>
        </div>
        <Link
          to="/user/children"
          className="text-xs font-semibold text-[#8189a0] hover:text-[#16213e]"
        >
         {" "}<ArrowLeft className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}Back to all children
        </Link>
      </div>
    </div>
  );
}
